import { useEffect, useState } from "react";
import { CheckCircle2, Database, Globe, Server, Zap } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import StatCard from "@/components/admin/StatCard";
import { adminApi, API_BASE } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

/**
 * /admin/system — Runtime & platform state.
 *
 * Verifies that key subsystems (API, DB via summary call, AutoSEO webhook)
 * respond. Also surfaces build-time constants so the operator can confirm
 * which environment they're actually inside.
 */

const START = Date.now();

function useUptime() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const secs = Math.floor((now - START) / 1000);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${h}h ${m}m ${s}s`;
}

export default function AdminSystem() {
  const { t } = useT();
  const [apiOk, setApiOk] = useState(null);
  const [seoOk, setSeoOk] = useState(null);
  const [checkedAt, setCheckedAt] = useState("");
  const uptime = useUptime();

  const runChecks = async () => {
    setApiOk(null); setSeoOk(null);
    try { const s = await adminApi.summary(); setApiOk(true); setSeoOk(!!s?.autoseo?.enabled); }
    catch { setApiOk(false); setSeoOk(false); }
    setCheckedAt(new Date().toLocaleTimeString());
  };

  useEffect(() => { runChecks(); }, []);

  const cell = (ok) => ok === null ? "…" : ok ? t("admin.label.ok") : t("admin.label.down");
  const accent = (ok) => ok === null ? "fog" : ok ? "green" : "red";

  return (
    <AdminShell title={t("admin.system.title")}>
      <p className="text-sm text-bone/60 -mt-6 mb-8 max-w-2xl">
        {t("admin.system.intro")}
      </p>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="system-status">
        <StatCard testId="sys-api"     icon={Server}       label={t("admin.system.api")}    value={cell(apiOk)} hint="/api/admin/stats/summary" accent={accent(apiOk)} />
        <StatCard testId="sys-db"      icon={Database}     label={t("admin.system.db")}     value={cell(apiOk)} hint="MongoDB via Motor" accent={accent(apiOk)} />
        <StatCard testId="sys-webhook" icon={Zap}          label={t("admin.system.webhook")} value={cell(seoOk)} hint={t("admin.label.configured")} accent={accent(seoOk)} />
        <StatCard testId="sys-uptime"  icon={CheckCircle2} label={t("admin.system.uptime")} value={uptime} hint={t("admin.label.live")} accent="green" />
      </section>

      <section className="border border-gold/15 bg-white/[0.02] p-5 md:p-6 mb-10" data-testid="system-env">
        <h2 className="font-serif text-xl text-bone mb-4">{t("admin.system.env")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
          <div className="flex justify-between border-b border-white/5 py-2"><span className="text-bone/60">{t("admin.system.frontend")}</span><span className="text-bone/85">React 19 · Tailwind 3</span></div>
          <div className="flex justify-between border-b border-white/5 py-2"><span className="text-bone/60">{t("admin.system.backend")}</span><span className="text-bone/85">FastAPI · Motor</span></div>
          <div className="flex justify-between border-b border-white/5 py-2"><span className="text-bone/60">{t("admin.system.db")}</span><span className="text-bone/85">MongoDB</span></div>
          <div className="flex justify-between border-b border-white/5 py-2"><span className="text-bone/60">{t("admin.system.region")}</span><span className="text-bone/85">EU (Emergent)</span></div>
          <div className="flex justify-between border-b border-white/5 py-2"><span className="text-bone/60">{t("admin.settings.apiBase")}</span><span className="text-bone/85 truncate max-w-[200px]">{API_BASE}</span></div>
          <div className="flex justify-between border-b border-white/5 py-2"><span className="text-bone/60">{t("admin.system.lastCheck")}</span><span className="text-bone/85">{checkedAt || "—"}</span></div>
        </div>
        <button
          type="button"
          onClick={runChecks}
          data-testid="system-recheck"
          className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gold border border-gold/40 px-4 py-2 hover:bg-gold/10"
        >
          <Globe size={12} strokeWidth={1.5} /> {t("admin.action.retest")}
        </button>
      </section>
    </AdminShell>
  );
}
